import api from './api';

// Convert base64 VAPID public key to Uint8Array (required by browser API)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

// Check if push notifications are supported on this browser
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

// Request permission and subscribe the user
export async function subscribeToPush() {
  if (!isPushSupported()) {
    console.warn('Push notifications not supported on this browser.');
    return null;
  }

  // Ask user for permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    console.warn('Notification permission denied.');
    return null;
  }

  try {
    // Fetch VAPID public key from backend
    const { data } = await api.get('/api/pushsubscription/vapid-public-key');
    const applicationServerKey = urlBase64ToUint8Array(data.publicKey);

    // Get the active service worker
    const registration = await navigator.serviceWorker.ready;

    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly:      true,   // Required — must show a notification for every push
      applicationServerKey,
    });

    // Extract the keys from the subscription object
    const p256dh = btoa(
      String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))
    );
    const auth = btoa(
      String.fromCharCode(...new Uint8Array(subscription.getKey('auth')))
    );

    // Save subscription to backend
    await api.post('/api/pushsubscription/subscribe', {
      endpoint: subscription.endpoint,
      p256dh,
      auth,
    });

    console.log('Push subscription saved successfully.');
    return subscription;
  } catch (err) {
    console.error('Failed to subscribe to push:', err);
    return null;
  }
}

// Check if already subscribed; if not, subscribe (prevents hitting backend on every load)
export async function subscribeToPushIfNeeded() {
  if (!isPushSupported()) return null;

  try {
    const registration   = await navigator.serviceWorker.ready;
    const existingSub    = await registration.pushManager.getSubscription();

    // Already subscribed — no need to hit backend again
    if (existingSub) {
      console.log('Already subscribed to push notifications.');
      return existingSub;
    }

    // Not subscribed yet — request permission and register
    return await subscribeToPush();
  } catch (err) {
    console.warn('Failed to check push subscription status:', err);
    return null;
  }
}

// Unsubscribe user from push notifications
export async function unsubscribeFromPush() {
  try {
    const registration  = await navigator.serviceWorker.ready;
    const subscription  = await registration.pushManager.getSubscription();

    if (subscription) {
      await api.post('/api/pushsubscription/unsubscribe', {
        endpoint: subscription.endpoint,
      });
      await subscription.unsubscribe();
      console.log('Unsubscribed from push notifications.');
    }
  } catch (err) {
    console.error('Failed to unsubscribe:', err);
  }
}
